/**
 * Facade over the API modules in `api/` (FE Arch 1.0 Step 1, #1165).
 *
 * Endpoint code lives in `api/<service>.ts`, one module per backend service. This file keeps
 * `BrainFocusApi` — one static member per endpoint, each replaceable with
 * `sinon.stub(BrainFocusApi, name)` — and the types of the API modules. It is the stubbable
 * entry point to the backend for components and tests, not a migration shim (#1170): domain
 * types come from `domain/`, the transport and its errors from `api/client`.
 */
import * as signupApi from '@brainfocus/web/api/signup';
import * as userAdminApi from '@brainfocus/web/api/admin/user_admin';
import * as edfApi from '@brainfocus/web/api/edf';
import * as trcVideoApi from '@brainfocus/web/api/trc_video';
import * as mriApi from '@brainfocus/web/api/mri';
import * as analysisApi from '@brainfocus/web/api/analysis';
import * as visualizationApi from '@brainfocus/web/api/visualization';
import * as channelLayoutApi from '@brainfocus/web/api/channel_layout';
import * as patientChannelLayoutApi from '@brainfocus/web/api/patient_channel_layout';
import * as recordingApi from '@brainfocus/web/api/recording';
import * as annotationApi from '@brainfocus/web/api/annotation';
import * as edfPositionApi from '@brainfocus/web/api/edf_position';
import * as electrodeValidationApi from '@brainfocus/web/api/electrode_validation';
import * as patientApi from '@brainfocus/web/api/patient';
import * as coregistrationApi from '@brainfocus/web/api/coregistration';
import * as neuroimageApi from '@brainfocus/web/api/neuroimage';
import * as acceptanceApi from '@brainfocus/web/api/acceptance';
import * as ndaAdminApi from '@brainfocus/web/api/admin/nda_admin';
import * as demoAdminApi from '@brainfocus/web/api/admin/demo_admin';
import * as auditLogApi from '@brainfocus/web/api/audit_log';
import * as dashboardApi from '@brainfocus/web/api/dashboard';
import * as featureFlagApi from '@brainfocus/web/api/feature_flag';

export type {
    SignupPayload, SignupResult, TrialSettings, UpdateTrialSettingsPayload,
} from '@brainfocus/web/api/signup';
export { UserRole } from '@brainfocus/web/api/admin/user_admin';
export type {
    ManagedUser, SaveUserPayload, ManagedInstitution, SaveInstitutionPayload,
} from '@brainfocus/web/api/admin/user_admin';
export type {
    UploadEdfPayload, UploadEdfResult, EdfPreviewResult,
} from '@brainfocus/web/api/edf';
export type {
    TrcVideoUpload, TrcVideoUploadHooks, UploadTrcVideosPayload, TrcVideosResult,
} from '@brainfocus/web/api/trc_video';
export type {
    UploadMriPayload, MriRecord,
} from '@brainfocus/web/api/mri';
export type {
    GetAnalysesPayload, EphemeralQuota, AnalysisDetails, RunAnalysisPayload, EdfMeta, CalcMeta,
    BaselineTier, BaselineInfo, UpdatableAnalysisInfo,
} from '@brainfocus/web/api/analysis';
export { VolumeLoadError } from '@brainfocus/web/api/visualization';
export type {
    GaaeResult, AutomaticDetectionResponse, SozRow,
} from '@brainfocus/web/api/visualization';
export type {
    ChannelLayoutResponse,
} from '@brainfocus/web/api/channel_layout';
export type {
    ApiRecordingInfo,
} from '@brainfocus/web/api/recording';
export type {
    UserAnnotationInput,
} from '@brainfocus/web/api/annotation';
export type {
    EdfPosition,
} from '@brainfocus/web/api/edf_position';
export type {
    ElectrodeDecision, ElectrodeValidation, ElectrodeValidationInput,
} from '@brainfocus/web/api/electrode_validation';
export type {
    PatientUpsert,
} from '@brainfocus/web/api/patient';
export type {
    CoregistrationStatusItem, CoregistrationStatusResponse, UploadCoregistrationPayload,
    UploadCoregistrationResult, CoregistrationRequest, CoregistrationFile,
} from '@brainfocus/web/api/coregistration';
export type {
    UploadNeuroimagePayload, UploadNeuroimageResult,
} from '@brainfocus/web/api/neuroimage';
export type {
    NdaAccount,
} from '@brainfocus/web/api/admin/nda_admin';
export type {
    DemoSettings,
} from '@brainfocus/web/api/admin/demo_admin';
export type {
    AuditLogEntry, AuditLogFilters, AuditLogResponse,
} from '@brainfocus/web/api/audit_log';
export type {
    DashboardRecording, DashboardNeuroimage, DashboardPatient, DashboardResponse,
} from '@brainfocus/web/api/dashboard';
export type {
    FeatureFlagDefinition, FeatureFlagOverridesByFlag, UserFeatureFlagRow,
    InstitutionFeatureFlagRow,
} from '@brainfocus/web/api/feature_flag';
export { allNeuroimageFiles } from '@brainfocus/web/api/types';
export type {
    AnalysisDetail, AnalysisStatus, DetectionMethod, SeizureGroup, ElectrodeColormapRange,
    ElectrodeColormapRanges, Analysis, Recording, TrcVideoRecord, GetEegPayload, ListManagedPayload,
    EdfInfo, RawEdfAnnotation, PatientMri, Patient, NeuroimageFileType, CoregistrationStatusValue,
    ConversionStatus, NeuroimageFile, NeuroimageFilesResponse, CoregistrationResult,
    NotificationEmail, DashboardAnalysis,
} from '@brainfocus/web/api/types';

/**
 * Class exposing static methods to access any of the BrainFocus API endpoints.
 */
export class BrainFocusApi {

    public static signup = signupApi.signup;
    public static getSurvey = signupApi.getSurvey;
    public static saveInstitution = userAdminApi.saveInstitution;
    public static getInstitutions = userAdminApi.getInstitutions;
    public static uploadEdf = edfApi.uploadEdf;
    public static uploadTrcVideos = trcVideoApi.uploadTrcVideos;
    public static getTrcVideos = trcVideoApi.getTrcVideos;
    public static trcVideoStreamUrl = trcVideoApi.trcVideoStreamUrl;
    public static uploadMri = mriApi.uploadMri;
    public static getPatientMris = mriApi.getPatientMris;
    public static deleteMri = mriApi.deleteMri;
    public static deleteMriByUploadRef = mriApi.deleteMriByUploadRef;
    public static uploadSegmentation = mriApi.uploadSegmentation;
    public static uploadOverlay = mriApi.uploadOverlay;
    public static uploadPostImplantation = mriApi.uploadPostImplantation;
    public static changeOverlayRole = mriApi.changeOverlayRole;
    public static deleteOverlay = mriApi.deleteOverlay;
    public static deleteSegmentation = mriApi.deleteSegmentation;
    public static assignMriToAnalysis = mriApi.assignMriToAnalysis;
    public static runAnalysis = analysisApi.runAnalysis;
    public static createPendingAnalysis = analysisApi.createPendingAnalysis;
    public static automaticDetection = visualizationApi.automaticDetection;
    public static recompute = analysisApi.recompute;
    public static getChannelLayout = channelLayoutApi.getChannelLayout;
    public static putChannelLayout = channelLayoutApi.putChannelLayout;
    public static deleteChannelLayout = channelLayoutApi.deleteChannelLayout;
    public static listPatientChannelLayouts = patientChannelLayoutApi.listPatientChannelLayouts;
    public static getPatientChannelLayout = patientChannelLayoutApi.getPatientChannelLayout;
    public static createPatientChannelLayout = patientChannelLayoutApi.createPatientChannelLayout;
    public static updatePatientChannelLayout = patientChannelLayoutApi.updatePatientChannelLayout;
    public static deletePatientChannelLayout = patientChannelLayoutApi.deletePatientChannelLayout;
    public static abortAnalysis = analysisApi.abortAnalysis;
    public static retryAnalysis = analysisApi.retryAnalysis;
    public static recomputeToMovingZscore = analysisApi.recomputeToMovingZscore;
    public static getRecordingInfo = recordingApi.getRecordingInfo;
    public static updateAnalysis = analysisApi.updateAnalysis;
    public static deleteAnalysis = analysisApi.deleteAnalysis;
    public static listAnnotations = annotationApi.listAnnotations;
    public static createAnnotation = annotationApi.createAnnotation;
    public static updateAnnotation = annotationApi.updateAnnotation;
    public static getEdfPosition = edfPositionApi.getEdfPosition;
    public static saveEdfPosition = edfPositionApi.saveEdfPosition;
    public static deleteAnnotation = annotationApi.deleteAnnotation;
    public static listElectrodeValidations = electrodeValidationApi.listElectrodeValidations;
    public static saveElectrodeValidation = electrodeValidationApi.saveElectrodeValidation;
    public static electrodeValidationsCsvUrl = electrodeValidationApi.electrodeValidationsCsvUrl;
    public static deleteUser = userAdminApi.deleteUser;
    public static deleteInstitution = userAdminApi.deleteInstitution;
    public static deletePatient = patientApi.deletePatient;
    public static getEeg = visualizationApi.getEeg;
    public static getEdfPreview = edfApi.getEdfPreview;
    public static downloadNpy = analysisApi.downloadNpy;
    public static getTcPlot = visualizationApi.getTcPlot;
    public static getBaseline = visualizationApi.getBaseline;
    public static getBrain3dInputs = visualizationApi.getBrain3dInputs;
    public static getMri3dInputs = visualizationApi.getMri3dInputs;
    public static getMriTiltCorrection = visualizationApi.getMriTiltCorrection;
    public static getNeuroimage3dInputs = visualizationApi.getNeuroimage3dInputs;
    public static getGaae = visualizationApi.getGaae;
    public static calculateGaae = visualizationApi.calculateGaae;
    public static setEdfBadChannels = edfApi.setEdfBadChannels;
    public static edfInfo = edfApi.edfInfo;
    public static getAnalysis = analysisApi.getAnalysis;
    public static getAnalyses = analysisApi.getAnalyses;
    public static getRecordings = recordingApi.getRecordings;
    public static getEphemeralQuota = analysisApi.getEphemeralQuota;
    public static deleteRecording = recordingApi.deleteRecording;
    public static updateRecording = recordingApi.updateRecording;
    public static getPatients = patientApi.getPatients;
    public static getPatient = patientApi.getPatient;
    public static getPatientsSeizures = recordingApi.getPatientsSeizures;
    public static setPatient = patientApi.setPatient;
    public static createCoregistration = coregistrationApi.createCoregistration;
    public static uploadNeuroimage = neuroimageApi.uploadNeuroimage;
    public static getNeuroimageFiles = neuroimageApi.getNeuroimageFiles;
    public static deleteNeuroimageFile = neuroimageApi.deleteNeuroimageFile;
    public static downloadNeuroimageFile = neuroimageApi.downloadNeuroimageFile;
    public static getCoregistrationStatus = coregistrationApi.getCoregistrationStatus;
    public static uploadCoregistrationResults = coregistrationApi.uploadCoregistrationResults;
    public static updateCoregistrationStatus = coregistrationApi.updateCoregistrationStatus;
    public static getUsers = userAdminApi.getUsers;
    public static getInstitutionMap = userAdminApi.getInstitutionMap;
    public static saveUser = userAdminApi.saveUser;
    public static acceptTerms = acceptanceApi.acceptTerms;
    public static updatePassword = userAdminApi.updatePassword;
    public static getCoregistrationRequests = coregistrationApi.getCoregistrationRequests;
    public static markCoregistrationInProgress = coregistrationApi.markCoregistrationInProgress;
    public static deleteCoregistration = coregistrationApi.deleteCoregistration;
    public static getNotificationEmails = coregistrationApi.getNotificationEmails;
    public static addNotificationEmail = coregistrationApi.addNotificationEmail;
    public static deleteNotificationEmail = coregistrationApi.deleteNotificationEmail;
    public static getTrialNotificationEmails = signupApi.getTrialNotificationEmails;
    public static addTrialNotificationEmail = signupApi.addTrialNotificationEmail;
    public static deleteTrialNotificationEmail = signupApi.deleteTrialNotificationEmail;
    public static getTrialSettings = signupApi.getTrialSettings;
    public static updateTrialSettings = signupApi.updateTrialSettings;
    public static getNdaAccounts = ndaAdminApi.getNdaAccounts;
    public static verifyNda = ndaAdminApi.verifyNda;
    public static ndaDocumentUrl = ndaAdminApi.ndaDocumentUrl;
    public static toggleDemoAnalysis = demoAdminApi.toggleDemoAnalysis;
    public static getDemoSettings = demoAdminApi.getDemoSettings;
    public static setDemoInstitution = demoAdminApi.setDemoInstitution;
    public static getAuditLogs = auditLogApi.getAuditLogs;
    public static exportAuditLogsCsv = auditLogApi.exportAuditLogsCsv;
    public static getDashboard = dashboardApi.getDashboard;
    public static getFeatureFlagRegistry = featureFlagApi.getFeatureFlagRegistry;
    public static getFeatureFlagOverview = featureFlagApi.getFeatureFlagOverview;
    public static getFeatureFlagOverrides = featureFlagApi.getFeatureFlagOverrides;
    public static getUserFeatureFlags = featureFlagApi.getUserFeatureFlags;
    public static getInstitutionFeatureFlags = featureFlagApi.getInstitutionFeatureFlags;
    public static setUserFeatureFlag = featureFlagApi.setUserFeatureFlag;
    public static clearUserFeatureFlag = featureFlagApi.clearUserFeatureFlag;
    public static setInstitutionFeatureFlag = featureFlagApi.setInstitutionFeatureFlag;
    public static clearInstitutionFeatureFlag = featureFlagApi.clearInstitutionFeatureFlag;

}
